import React from 'react';
import { Shield, Landmark, Percent, CalendarCheck, FileCheck, CheckCircle2, AlertTriangle, ArrowLeft, Download, Mail, Phone, MapPin } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import { API_URL as API } from '../config/api';

export default function PartnerAgreement() {
  const navigate = useNavigate();

  return (
    <div style={{ background: '#F8FAF7', minHeight: '100vh', fontFamily: "'DM Sans', sans-serif", paddingBottom: '4rem' }}>
      {/* Header Banner */}
      <div style={{
        background: '#084734',
        color: 'white',
        padding: '3.5rem 2rem',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          width: '250px',
          height: '250px',
          borderRadius: '50%',
          background: 'rgba(206, 241, 123, 0.08)',
          pointerEvents: 'none'
        }} />
        <div style={{ maxWidth: '840px', margin: '0 auto' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(206, 241, 123, 0.15)',
            border: '1px solid rgba(206, 241, 123, 0.3)',
            padding: '6px 16px',
            borderRadius: '50px',
            color: '#CEF17B',
            fontSize: '0.85rem',
            fontWeight: '700',
            marginBottom: '1rem'
          }}>
            <Shield size={16} color="#CEF17B" /> Venue Operator & Merchant Contract
          </div>
          <h1 style={{ fontSize: '2.4rem', fontWeight: '900', margin: '0 0 0.75rem 0', letterSpacing: '-0.5px' }}>
            Venue Partner & Settlement Agreement
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '1rem', lineHeight: '1.6', margin: 0 }}>
            Master Terms for Sports Turf Operators, Booking Fulfillments, KYC Bank Details & Revenue Settlements
          </p>
        </div>
      </div>

      {/* Main Content Container */}
      <div style={{ maxWidth: '920px', margin: '-2rem auto 0 auto', padding: '0 1.5rem' }}>
        <div style={{ marginBottom: '1rem' }}>
          <BackButton />
        </div>

        <div style={{
          background: 'white',
          borderRadius: '24px',
          padding: '2.5rem',
          boxShadow: '0 10px 40px rgba(0,0,0,0.06)',
          border: '1.5px solid #EEF2E6'
        }}>
          {/* Quick summary notice */}
          <div style={{
            background: '#F0FDF4',
            border: '1.5px solid #BBF7D0',
            borderRadius: '16px',
            padding: '1.25rem 1.5rem',
            marginBottom: '2rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#166534', fontWeight: '800', fontSize: '0.95rem' }}>
                <CheckCircle2 size={18} /> Legally Enforceable Merchant Contract
              </div>
              <p style={{ margin: '4px 0 0 0', color: '#15803D', fontSize: '0.85rem', lineHeight: 1.5 }}>
                By registering as a Venue Partner on TurfX, you provide affirmative electronic consent to this commercial settlement agreement.
              </p>
            </div>
            <a
              href={`${API}/agreements/download/template_partner`}
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: '#084734',
                color: '#CEF17B',
                padding: '8px 16px',
                borderRadius: '10px',
                fontSize: '0.82rem',
                fontWeight: '800',
                textDecoration: 'none'
              }}
            >
              <Download size={14} /> Download Sample PDF
            </a>
          </div>

          {/* Section 1 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#084734', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FileCheck size={20} color="#084734" /> 1. Merchant Onboarding, Listing Accuracy & Operational Standards
            </h2>
            <p style={{ color: '#475569', lineHeight: '1.8', fontSize: '0.95rem', margin: 0 }}>
              This Venue Partner and Settlement Agreement ("Agreement") governs the listing, scheduling, fee settlement, and operational commitments between your sports facility business ("Partner", "Venue Operator", or "Merchant") and <strong>TurfX</strong> (operated by <strong>MetaQode Technologies Pvt. Ltd.</strong>). By registering as a partner, you agree to list genuine sports facilities, accurate court dimensions, artificial turf/court specifications (FIFA approved turf, wooden badminton, acrylic tennis), realistic photographs, and transparent hourly slot pricing inclusive of applicable statutory taxes.
            </p>
          </section>

          <hr style={{ border: 'none', borderTop: '1px dashed #E2E8F0', margin: '2rem 0' }} />

          {/* Section 2 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#084734', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Shield size={20} color="#084734" /> 2. Information We Collect from Venue Partners
            </h2>
            <p style={{ color: '#475569', lineHeight: '1.8', fontSize: '0.95rem', marginBottom: '1rem' }}>
              To facilitate merchant settlement, tax invoicing, fraud prevention, and player safety, we collect and process the following verified business data:
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem' }}>
              <div style={{ background: '#F8FAF7', border: '1px solid #EEF2E6', padding: '1.2rem', borderRadius: '14px' }}>
                <h4 style={{ margin: '0 0 6px 0', color: '#084734', fontWeight: '800', fontSize: '0.92rem' }}>🏢 Business Entity & KYC Data</h4>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748B', lineHeight: '1.6' }}>
                  Registered trade name, proprietor/partner/director credentials (mobile, email), and business identity verification (PAN / GSTIN numbers).
                </p>
              </div>
              <div style={{ background: '#F8FAF7', border: '1px solid #EEF2E6', padding: '1.2rem', borderRadius: '14px' }}>
                <h4 style={{ margin: '0 0 6px 0', color: '#084734', fontWeight: '800', fontSize: '0.92rem' }}>🏟 Facility, Dimensions & Pricing</h4>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748B', lineHeight: '1.6' }}>
                  Exact ground dimensions, sports surfaces (5-a-side football turf, box cricket, synthetic clay), GPS coordinates, high-resolution photographs, and slot rate charts.
                </p>
              </div>
              <div style={{ background: '#F8FAF7', border: '1px solid #EEF2E6', padding: '1.2rem', borderRadius: '14px' }}>
                <h4 style={{ margin: '0 0 6px 0', color: '#084734', fontWeight: '800', fontSize: '0.92rem' }}>🏦 Mandatory Bank Account Particulars</h4>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748B', lineHeight: '1.6' }}>
                  Bank account number, verified account holder name, IFSC code, and bank branch to execute automated booking earnings payout disbursements.
                </p>
              </div>
              <div style={{ background: '#F8FAF7', border: '1px solid #EEF2E6', padding: '1.2rem', borderRadius: '14px' }}>
                <h4 style={{ margin: '0 0 6px 0', color: '#084734', fontWeight: '800', fontSize: '0.92rem' }}>📱 QR Gate Check-In & Audit Logs</h4>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748B', lineHeight: '1.6' }}>
                  Timestamped check-in records when ground staff scans player QR passes, validating player arrival, court fulfillment, and settlement releases.
                </p>
              </div>
            </div>
          </section>

          <hr style={{ border: 'none', borderTop: '1px dashed #E2E8F0', margin: '2rem 0' }} />

          {/* Section 3 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#084734', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Landmark size={20} color="#084734" /> 3. Why Bank Account Details Are Mandatory in Vendor Signup
            </h2>
            <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', padding: '1.25rem', borderRadius: '14px', color: '#1E40AF', fontSize: '0.9rem', lineHeight: '1.7', marginBottom: '1rem' }}>
              <strong>⚠️ Essential Requirement:</strong> Submitting verified bank account details (Account Number & IFSC Code) is mandatory during partner onboarding. Without valid bank verification, TurfX cannot release customer booking revenues, process automated settlements, or disburse cancellation compensations.
            </div>
            <p style={{ color: '#475569', lineHeight: '1.8', fontSize: '0.95rem' }}>
              Why do we collect and verify your merchant bank details?
            </p>
            <ul style={{ color: '#475569', lineHeight: '1.8', fontSize: '0.95rem', paddingLeft: '1.5rem', margin: 0 }}>
              <li><strong>Automated Commercial Payouts:</strong> When customers book and complete matches at your venue, funds collected via encrypted platform payment gateways are credited to your partner wallet and disbursed directly to your designated bank account on automated settlement cycles.</li>
              <li><strong>GST & Tax Statutory Compliance:</strong> In accordance with Indian tax laws (TDS under Section 194-O of the Income Tax Act, 1961), electronic payout disbursement requires verified merchant bank accounts and audited financial logs.</li>
              <li><strong>Cancellation Compensation Payouts:</strong> When a customer cancels a booking within the non-refundable 6-hour window, 50% of the booking amount is automatically credited and disbursed to your bank account as operational loss compensation.</li>
            </ul>
          </section>

          <hr style={{ border: 'none', borderTop: '1px dashed #E2E8F0', margin: '2rem 0' }} />

          {/* Section 4 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#084734', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Percent size={20} color="#084734" /> 4. 5% Merchant Platform Commission & Bank Payout Settlements
            </h2>
            <ul style={{ color: '#475569', lineHeight: '1.8', fontSize: '0.95rem', paddingLeft: '1.5rem', margin: 0 }}>
              <li><strong>5% Merchant Commission:</strong> TurfX retains a transparent platform commission of <strong>5% platform fee</strong> (+ 18% GST on platform fee) from the gross court fee on each fulfilled booking for facilitating online marketing, server infrastructure, customer acquisition, and payment processing.</li>
              <li><strong>Encrypted Gateway Processing:</strong> Online payments are secured through RBI-authorized, PCI-DSS Level 1 certified payment gateways. TurfX absorbs standard payment collection and chargeback risks for valid bookings.</li>
              <li><strong>Settlement Schedule:</strong> Payout balances are reconciled automatically upon QR code check-in validation and disbursed to your registered bank account via automated IMPS/NEFT transfers according to the active settlement schedule.</li>
            </ul>
          </section>

          <hr style={{ border: 'none', borderTop: '1px dashed #E2E8F0', margin: '2rem 0' }} />

          {/* Section 5 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#084734', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FileCheck size={20} color="#084734" /> 5. Tournament Hosting & 5% Facilitation Commission
            </h2>
            <p style={{ color: '#475569', lineHeight: '1.8', fontSize: '0.95rem' }}>
              For venue partners hosting or organizing sports tournaments on the TurfX platform:
            </p>
            <ul style={{ color: '#475569', lineHeight: '1.8', fontSize: '0.95rem', paddingLeft: '1.5rem', margin: 0 }}>
              <li><strong>Tournament Registration Fee Collection:</strong> TurfX collects entry fees from participating teams and players through encrypted gateways.</li>
              <li><strong>5% Tournament Commission:</strong> TurfX retains a <strong>5% tournament facilitation commission</strong> from the gross tournament registration fees to cover digital draw generation, referee/match scorecards, leaderboard displays, and escrow prize pool settlement.</li>
              <li><strong>Prize Pool & Host Disbursals:</strong> The net tournament revenue and prize pool allocations are disbursed directly to verified merchant/winner bank accounts following tournament completion.</li>
            </ul>
          </section>

          <hr style={{ border: 'none', borderTop: '1px dashed #E2E8F0', margin: '2rem 0' }} />

          {/* Section 6 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#084734', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <CalendarCheck size={20} color="#084734" /> 6. Slot Honor Guarantee, Overbooking Prohibition & Venue Standards
            </h2>
            <p style={{ color: '#475569', lineHeight: '1.8', fontSize: '0.95rem' }}>
              Venue partners must guarantee that every confirmed booking made via TurfX is honored unconditionally:
            </p>
            <ul style={{ color: '#475569', lineHeight: '1.8', fontSize: '0.95rem', paddingLeft: '1.5rem', margin: 0 }}>
              <li><strong>Zero Overbooking:</strong> Offline booking duplicates or refusing verified TurfX players entry at the venue gate is strictly prohibited and constitutes grounds for merchant penalties or platform delisting.</li>
              <li><strong>Facility Upkeep & Safety:</strong> Partners agree to maintain high facility standards including clean artificial grass fibers, balanced rubber infill, functional LED floodlights for night matches, safe boundary netting, accessible first aid kits, hygienic drinking water, and clean restroom facilities.</li>
              <li><strong>Staff Training & QR Pass Validation:</strong> Ground managers must scan customer QR match passes promptly at entry to record attendance and ensure seamless game access.</li>
            </ul>
          </section>

          <hr style={{ border: 'none', borderTop: '1px dashed #E2E8F0', margin: '2rem 0' }} />

          {/* Section 7 */}
          <section style={{ marginBottom: '2.5rem' }}>
            <h2 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#084734', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Shield size={20} color="#084734" /> 7. Indemnity, Merchant Liability & Jurisdiction
            </h2>
            <p style={{ color: '#475569', lineHeight: '1.8', fontSize: '0.95rem' }}>
              Venue partners agree to indemnify and hold harmless TurfX and its parent entity, MetaQode Technologies Pvt. Ltd., against any claims, damages, on-ground accidents, or physical injuries sustained on the venue premises. This Agreement is governed by the laws of India, with exclusive jurisdiction in the competent courts of Chitradurga / Bengaluru, Karnataka.
            </p>
          </section>

          <hr style={{ border: 'none', borderTop: '1px dashed #E2E8F0', margin: '2rem 0' }} />

          {/* Section 8: Policy Links */}
          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.3rem', fontWeight: '800', color: '#084734', marginBottom: '0.75rem' }}>
              8. Incorporation of Privacy Policy & Terms of Service
            </h2>
            <p style={{ color: '#475569', lineHeight: '1.8', fontSize: '0.95rem' }}>
              This Venue Partner Agreement explicitly incorporates our complete platform policies:
            </p>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '1rem' }}>
              <Link
                to="/privacy-policy"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: '#F8FAF7',
                  border: '1.5px solid #084734',
                  color: '#084734',
                  padding: '10px 18px',
                  borderRadius: '10px',
                  fontWeight: '700',
                  fontSize: '0.88rem',
                  textDecoration: 'none'
                }}
              >
                📄 See Full Privacy Policy →
              </Link>
              <Link
                to="/terms-and-conditions"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: '#F8FAF7',
                  border: '1.5px solid #084734',
                  color: '#084734',
                  padding: '10px 18px',
                  borderRadius: '10px',
                  fontWeight: '700',
                  fontSize: '0.88rem',
                  textDecoration: 'none'
                }}
              >
                📋 See Terms & Conditions →
              </Link>
            </div>
          </section>

          {/* Electronic Acknowledgment & Consent Box */}
          <div style={{
            background: '#F0FDF4',
            border: '2px solid #86EFAC',
            borderRadius: '18px',
            padding: '1.5rem 1.75rem',
            marginTop: '2rem',
            boxShadow: '0 4px 15px rgba(22, 163, 74, 0.08)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#166534', fontWeight: '900', fontSize: '1.05rem', marginBottom: '8px' }}>
              <CheckCircle2 size={22} color="#16a34a" /> Electronic Consent & Acceptance Statement
            </div>
            <p style={{ margin: '0 0 10px 0', color: '#15803D', fontSize: '0.98rem', fontWeight: '700', lineHeight: '1.6' }}>
              "I have read and acknowledge that I accept the above terms and conditions."
            </p>
            <p style={{ margin: 0, color: '#166534', fontSize: '0.82rem', lineHeight: '1.5' }}>
              By registering as a Venue Partner, onboarding sports grounds, and submitting bank payout credentials on TurfX, this electronic acceptance forms a binding commercial agreement under the Information Technology Act, 2000 (India).
            </p>
          </div>

          {/* MetaQode Address & Contact Details Section */}
          <div style={{ background: '#084734', color: 'white', borderRadius: '18px', padding: '1.75rem 2rem', display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', borderBottom: '1px solid rgba(255,255,255,0.15)', paddingBottom: '10px' }}>
              <div style={{ fontWeight: '900', fontSize: '1.05rem', color: '#CEF17B' }}>
                MetaQode Technologies Pvt. Ltd.
              </div>
              <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', background: 'rgba(255,255,255,0.1)', padding: '3px 10px', borderRadius: '6px' }}>
                Parent & Operating Entity of TurfX
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem', marginTop: '4px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '0.88rem', color: '#E2E8F0', lineHeight: 1.5 }}>
                <MapPin size={18} color="#CEF17B" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <strong style={{ color: 'white' }}>Registered Corporate Office:</strong><br />
                  MetaQode Technologies Pvt. Ltd.,<br />
                  Chitradurga - 577501, Karnataka, India
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.88rem', color: '#E2E8F0' }}>
                  <Mail size={16} color="#CEF17B" />
                  <span>Merchant Support: <a href="mailto:support@metaqode.co.in" style={{ color: '#CEF17B', textDecoration: 'none', fontWeight: '600' }}>support@metaqode.co.in</a></span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.88rem', color: '#E2E8F0' }}>
                  <Phone size={16} color="#CEF17B" />
                  <span>Partner Desk: <strong style={{ color: 'white' }}>+91 9980998199</strong></span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.88rem', color: '#E2E8F0' }}>
                  <Shield size={16} color="#CEF17B" />
                  <span>Official Website: <a href="https://metaqode.co.in" target="_blank" rel="noreferrer" style={{ color: '#CEF17B', textDecoration: 'none', fontWeight: '600' }}>https://metaqode.co.in</a></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
